import { useDebounce } from '@hooks/useDebounce';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormGroup,
    FormLabel,
    Slider,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import AutocompleteJobOffer, { JobOfferOption } from '@components/AutoComplete/AutocompleteJobOffer';
import AutoCompletePro from '@components/AutoComplete/AutoCompletePro';

const HERE_API_KEY = process.env.REACT_APP_PUBLIC_HERE_API_KEY;
const DEFAULT_LAT = 48.8566; // Paris
const DEFAULT_LNG = 2.3522;
const DEFAULT_RADIUS = 25;
const API_BASE = process.env.REACT_APP_PUBLIC_API_URI || 'http://localhost:4000';

export type LocationFilterValue = {
    lat: number;
    lng: number;
    radius: number;
    label?: string;
};

type AddressOption = {
    label: string;
    city?: string;
    postalCode?: string;
    lat?: number;
    lng?: number;
};

const fetchAddressSuggestions = (query: string, callback: (data: AddressOption[]) => void) => {
    if (!query || query.length < 2) {
        callback([]);
        return;
    }
    const isPostal = /^\d+$/.test(query);
    const endpoint = isPostal ? `postals/${encodeURIComponent(query)}` : `cities/${encodeURIComponent(query)}`;
    fetch(`${API_BASE}/here-map/${endpoint}`, { headers: { 'Content-Type': 'application/json' } })
        .then(res => res.json())
        .then(data => {
            const suggestions: AddressOption[] = (data.items ?? [])
                .filter((item: any) => !!item.address?.city && !!item.address?.postalCode)
                .map((item: any) => ({
                    label: `${item.address.city} (${item.address.postalCode})`,
                    city: item.address.city,
                    postalCode: item.address.postalCode,
                    lat: item.position?.lat,
                    lng: item.position?.lng,
                }));
            callback(suggestions);
        })
        .catch(() => callback([]));
};

const fetchGeocode = async (city: string, postalCode: string): Promise<{ lat: number; lng: number } | null> => {
    try {
        const query = encodeURIComponent(`city=${city};postalCode=${postalCode}`);
        const res = await fetch(`${API_BASE}/here-map/detail-by-city-postalcode/${query}`);
        const data = await res.json();
        if (data?.position) return { lat: data.position.lat, lng: data.position.lng };
        if (data?.items?.[0]?.position) return data.items[0].position;
        return null;
    } catch {
        return null;
    }
};

interface InputLocationFilterProps {
    name?: string;
}

const InputLocationFilter: React.FC<InputLocationFilterProps> = ({ name = 'location' }) => {
    const { setValue, watch } = useFormContext();
    const currentValue = watch(name) as LocationFilterValue | null;

    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [localLat, setLocalLat] = useState(DEFAULT_LAT);
    const [localLng, setLocalLng] = useState(DEFAULT_LNG);
    const [localRadius, setLocalRadius] = useState(DEFAULT_RADIUS);
    const [localLabel, setLocalLabel] = useState('');
    const [latInput, setLatInput] = useState(String(DEFAULT_LAT));
    const [lngInput, setLngInput] = useState(String(DEFAULT_LNG));

    const [addressQuery, setAddressQuery] = useState('');
    const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
    const [addressLoading, setAddressLoading] = useState(false);

    // Job offer selection state
    const [selectedJobOffer, setSelectedJobOffer] = useState<JobOfferOption | null>(null);
    const [selectedProId, setSelectedProId] = useState<string | null>(null);

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const circleRef = useRef<any>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const debouncedAddress = useDebounce(addressQuery, 500);

    const cleanupMap = useCallback(() => {
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;
        mapInstanceRef.current?.dispose();
        mapInstanceRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
    }, []);

    // Component unmount cleanup
    useEffect(() => () => cleanupMap(), [cleanupMap]);

    const handleOpen = () => {
        const vals = currentValue ?? { lat: DEFAULT_LAT, lng: DEFAULT_LNG, radius: DEFAULT_RADIUS, label: '' };
        setLocalLat(vals.lat);
        setLocalLng(vals.lng);
        setLocalRadius(vals.radius);
        setLocalLabel(vals.label || '');
        setLatInput(String(vals.lat));
        setLngInput(String(vals.lng));
        setIsOpen(true);
    };

    const handleClose = () => {
        cleanupMap();
        setIsOpen(false);
    };

    const handleApply = () => {
        const label = localLabel || `${localLat.toFixed(4)}, ${localLng.toFixed(4)}`;
        setValue(name, { lat: localLat, lng: localLng, radius: localRadius, label });
        cleanupMap();
        setIsOpen(false);
    };

    // Address suggestions effect
    useEffect(() => {
        if (!debouncedAddress || debouncedAddress.length < 2) {
            setAddressOptions([]);
            return;
        }
        setAddressLoading(true);
        fetchAddressSuggestions(debouncedAddress, data => {
            setAddressOptions(data);
            setAddressLoading(false);
        });
    }, [debouncedAddress]);

    // Map initialization — runs when dialog opens
    useEffect(() => {
        if (!isOpen) return;

        // Small delay so Dialog DOM has time to mount
        const timeoutId = setTimeout(() => {
            if (!mapRef.current || !HERE_API_KEY) return;

            let H: any;
            try {
                const mod = require('@here/maps-api-for-javascript');
                H = mod.default || mod;
            } catch {
                return;
            }

            const container = mapRef.current;
            requestAnimationFrame(() => {
                if (!container) return;

                const platform = new H.service.Platform({ apikey: HERE_API_KEY });
                const defaultLayers = platform.createDefaultLayers();
                const mapLayer = defaultLayers?.vector?.normal?.map ?? defaultLayers?.raster?.normal?.map;
                if (!mapLayer) return;

                const map = new H.Map(container, mapLayer, {
                    zoom: 10,
                    center: { lat: localLat, lng: localLng },
                });

                new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
                H.ui.UI.createDefault(map, defaultLayers);

                const marker = new H.map.Marker({ lat: localLat, lng: localLng }, { volatility: true });
                marker.draggable = true;

                const circle = new H.map.Circle(
                    { lat: localLat, lng: localLng },
                    localRadius * 1000,
                    {
                        style: {
                            strokeColor: 'rgba(66, 57, 176, 0.9)',
                            lineWidth: 2,
                            fillColor: 'rgba(66, 57, 176, 0.12)',
                        },
                    },
                );

                map.addObjects([circle, marker]);
                markerRef.current = marker;
                circleRef.current = circle;
                mapInstanceRef.current = map;

                // Click on map to set location
                map.addEventListener('tap', (evt: any) => {
                    if (evt.target instanceof H.map.Marker) return;
                    const coord = map.screenToGeo(evt.currentPointer.viewportX, evt.currentPointer.viewportY);
                    const newLat = parseFloat(coord.lat.toFixed(6));
                    const newLng = parseFloat(coord.lng.toFixed(6));
                    marker.setGeometry({ lat: newLat, lng: newLng });
                    circle.setCenter({ lat: newLat, lng: newLng });
                    setLocalLat(newLat);
                    setLocalLng(newLng);
                    setLatInput(String(newLat));
                    setLngInput(String(newLng));
                    setLocalLabel('');
                });

                map.addEventListener('dragstart', (evt: any) => {
                    if (evt.target instanceof H.map.Marker) {
                        map.getViewPort().element.style.cursor = 'grabbing';
                    }
                }, false);

                map.addEventListener('dragend', (evt: any) => {
                    if (evt.target instanceof H.map.Marker) {
                        map.getViewPort().element.style.cursor = 'auto';
                        const pos = evt.target.getGeometry();
                        const newLat = parseFloat(pos.lat.toFixed(6));
                        const newLng = parseFloat(pos.lng.toFixed(6));
                        circle.setCenter({ lat: newLat, lng: newLng });
                        setLocalLat(newLat);
                        setLocalLng(newLng);
                        setLatInput(String(newLat));
                        setLngInput(String(newLng));
                        setLocalLabel('');
                    }
                }, false);

                map.addEventListener('drag', (evt: any) => {
                    if (evt.target instanceof H.map.Marker) {
                        const coord = map.screenToGeo(evt.currentPointer.viewportX, evt.currentPointer.viewportY);
                        evt.target.setGeometry(coord);
                    }
                }, false);

                const ro = new ResizeObserver(() => map.getViewPort().resize());
                ro.observe(container);
                resizeObserverRef.current = ro;
            });
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reactively update circle radius without re-initializing the map
    useEffect(() => {
        if (circleRef.current) {
            circleRef.current.setRadius(localRadius * 1000);
        }
    }, [localRadius]);

    const updateMapPosition = useCallback((lat: number, lng: number) => {
        if (!mapInstanceRef.current || !markerRef.current || !circleRef.current) return;
        const coord = { lat, lng };
        markerRef.current.setGeometry(coord);
        circleRef.current.setCenter(coord);
        mapInstanceRef.current.setCenter(coord, true);
    }, []);

    const handleAddressSelect = async (_: any, option: AddressOption | null) => {
        if (!option) return;
        setLocalLabel(option.label);
        if (option.lat != null && option.lng != null) {
            setLocalLat(option.lat);
            setLocalLng(option.lng);
            setLatInput(String(option.lat));
            setLngInput(String(option.lng));
            updateMapPosition(option.lat, option.lng);
        } else if (option.city && option.postalCode) {
            const pos = await fetchGeocode(option.city, option.postalCode);
            if (pos) {
                setLocalLat(pos.lat);
                setLocalLng(pos.lng);
                setLatInput(String(pos.lat));
                setLngInput(String(pos.lng));
                updateMapPosition(pos.lat, pos.lng);
            }
        }
    };

    const handleLatInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLatInput(e.target.value);
        const val = parseFloat(e.target.value);
        if (!isNaN(val) && val >= -90 && val <= 90) {
            setLocalLat(val);
            setLocalLabel('');
            updateMapPosition(val, localLng);
        }
    };

    const handleLngInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLngInput(e.target.value);
        const val = parseFloat(e.target.value);
        if (!isNaN(val) && val >= -180 && val <= 180) {
            setLocalLng(val);
            setLocalLabel('');
            updateMapPosition(localLat, val);
        }
    };

    const handleJobOfferSelect = useCallback(
        async (jobOffer: JobOfferOption | null) => {
            if (!jobOffer) {
                setSelectedJobOffer(null);
                return;
            }

            setSelectedJobOffer(jobOffer);

            // Create label from job offer data
            const label = `${jobOffer.jobNumber} - ${jobOffer.title}`;
            setLocalLabel(label);

            // Try to geocode using city and postal code
            const pos = await fetchGeocode(jobOffer.city, jobOffer.postalCode);
            if (pos) {
                setLocalLat(pos.lat);
                setLocalLng(pos.lng);
                setLatInput(String(pos.lat));
                setLngInput(String(pos.lng));
                updateMapPosition(pos.lat, pos.lng);
            }
        },
        [updateMapPosition]
    );

    // Reset job offer selection when switching away from job offer tab
    useEffect(() => {
        if (activeTab !== 2) {
            setSelectedJobOffer(null);
            setSelectedProId(null);
        }
    }, [activeTab]);

    const hasLocation = Boolean(currentValue?.lat != null && currentValue?.lng != null);

    return (
        <>
            {/* Compact trigger shown inside FilterDrawer */}
            <Box
                onClick={handleOpen}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: theme =>
                        `1px solid ${hasLocation ? theme.palette.secondary.main : theme.palette.divider}`,
                    background: theme =>
                        hasLocation ? 'rgba(60, 45, 225, 0.08)' : theme.palette.dashboard.main,
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': {
                        borderColor: theme => theme.palette.secondary.main,
                        background: 'rgba(60, 45, 225, 0.05)',
                    },
                }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <i
                        className="fi fi-br-marker"
                        style={{
                            fontSize: '16px',
                            color: hasLocation ? '#4239B0' : undefined,
                            display: 'flex',
                        }}
                    />
                    <Box>
                        <Typography variant="body2" fontWeight={500} lineHeight={1.3}>
                            {hasLocation
                                ? currentValue.label || `${currentValue.lat?.toFixed(4)}, ${currentValue.lng?.toFixed(4)}`
                                : 'Zone géographique'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {hasLocation ? `Rayon : ${currentValue.radius} km` : 'Cliquez pour définir une zone'}
                        </Typography>
                    </Box>
                </Stack>
                <i
                    className="fi fi-br-pencil"
                    style={{ fontSize: '13px', opacity: 0.4, display: 'flex' }}
                />
            </Box>

            {/* Map Dialog */}
            <Dialog
                open={isOpen}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { height: '80vh', maxHeight: '700px', display: 'flex', flexDirection: 'column' } }}>
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <i className="fi fi-br-marker" style={{ fontSize: '18px', display: 'flex' }} />
                        Filtre par zone géographique
                    </Stack>
                </DialogTitle>

                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        overflow: 'hidden',
                        flex: 1,
                    }}>
                    {/* Reference point source tabs */}
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}>
                        <Tab label="Adresse" sx={{ minHeight: 36, py: 0.5, fontSize: '13px' }} />
                        <Tab label="Coordonnées" sx={{ minHeight: 36, py: 0.5, fontSize: '13px' }} />
                        <Tab label="Offre d'emploi" sx={{ minHeight: 36, py: 0.5, fontSize: '13px' }} />
                        <Tooltip title="Disponible prochainement — nécessite les coordonnées du client">
                            <span>
                                <Tab
                                    label="Client"
                                    disabled
                                    sx={{ minHeight: 36, py: 0.5, fontSize: '13px' }}
                                />
                            </span>
                        </Tooltip>
                    </Tabs>

                    {/* Tab: address autocomplete */}
                    {activeTab === 0 && (
                        <Autocomplete
                            options={addressOptions}
                            loading={addressLoading}
                            onInputChange={(_, value) => setAddressQuery(value)}
                            onChange={handleAddressSelect}
                            getOptionLabel={o => o.label}
                            isOptionEqualToValue={(a, b) => a.label === b.label}
                            noOptionsText={
                                addressQuery.length >= 2 ? 'Aucun résultat' : 'Tapez pour rechercher...'
                            }
                            loadingText="Chargement..."
                            size="small"
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    placeholder="Rechercher une ville ou un code postal..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {addressLoading ? (
                                                    <CircularProgress color="inherit" size={16} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    )}

                    {/* Tab: job offer autocomplete */}
                    {activeTab === 2 && (
                        <Stack gap={2} flexDirection="row" alignItems="center" width="100%">
                            <FormGroup sx={{ flex: 1, minWidth: 0 }}>
                                <FormLabel>
                                    Filtrer par Établissement
                                </FormLabel>
                                <AutoCompletePro
                                    size="small"
                                    onChange={(proId: string | null) => {
                                        setSelectedProId(proId);
                                        setSelectedJobOffer(null);
                                    }}
                                />
                            </FormGroup>
                            <FormGroup sx={{ flex: 1, minWidth: 0 }}>
                                <FormLabel>
                                    Filtrer par Offre d'emploi
                                </FormLabel>
                                <AutocompleteJobOffer
                                    key={selectedProId || 'all-pros'}
                                    proId={selectedProId}
                                    onSelect={handleJobOfferSelect}
                                    size="small"
                                />
                            </FormGroup>
                        </Stack>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                        Cliquez sur la carte ou faites glisser le marqueur pour définir le point de référence.
                    </Typography>

                    {/* HERE Map */}
                    <Box
                        ref={mapRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: '100%',
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    />

                    {/* Radius slider */}
                    <Box sx={{ px: 1, pb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500} gutterBottom>
                            Rayon : <strong>{localRadius} km</strong>
                        </Typography>
                        <Slider
                            value={localRadius}
                            onChange={(_, v) => setLocalRadius(v as number)}
                            min={1}
                            max={200}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={v => `${v} km`}
                            marks={[
                                { value: 1, label: '1 km' },
                                { value: 50, label: '50 km' },
                                { value: 100, label: '100 km' },
                                { value: 200, label: '200 km' },
                            ]}
                            sx={{
                                color: theme => theme.palette.secondary.main,
                                '& .MuiSlider-markLabel': { fontSize: '11px' },
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ gap: 1 }}>
                    <Button onClick={handleClose} variant="text" color="secondary" size="small">
                        Annuler
                    </Button>
                    <Button onClick={handleApply} color="primary" variant="contained" size="small">
                        Appliquer
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default InputLocationFilter;
